import { graphHooks } from '@tunasong/graph-lib/react'
import { Poll as PollIcon } from '@tunasong/icons'
import { useCurrentUser } from '@tunasong/redux'
import type { ManyOfManyPollResponse } from '@tunasong/schemas'
import { isManyOfManyPoll, isPersisted, isPoll } from '@tunasong/schemas'
import {  EntityLinkById, Typography, Button, FormGroup, FormControl, FormControlLabel} from '@tunasong/ui-lib'
import type { FC } from 'react'
import React, { useMemo } from 'react'
import invariant from 'tiny-invariant'
import type { PollProps } from '../poll.js'
import styles from '../poll.module.css'
import { getProgress } from '../progress-util.js'
import Progress from '../progress.js'
import { sortPollOptionsIndices } from '../sort.js'
import VoteButton from '../vote-button.js'

export const ManyOfManyPollVote: FC<PollProps> = props => {
  const { poll, userId, onChange, sort } = props

  invariant(isManyOfManyPoll(poll), `Poll must be a ManyOfManyPoll`)

  const { userId: currentUserId } = useCurrentUser()
  const { profile: respondent } = graphHooks.useProfile(userId)
  const { createEntity, isCreatingEntity, hasCreatedEntity } = graphHooks.useEntityGraph()

  const { entities: allResponses = [], isSuccess: hasLoadedChildren } =
    graphHooks.useEntityChildren<ManyOfManyPollResponse>({
      parentId: poll.id,
      filter: 'pollresponse',
    })

  const finalResponse = useMemo(
    () => allResponses.filter(response => response.userId === userId)[0]?.choices,
    [allResponses, userId]
  )

  const [currentResponses, setCurrentResponses] = React.useState<Record<number, boolean>>({})

  const isExternalRespondent = userId !== currentUserId

  invariant(isManyOfManyPoll(poll), 'Poll must be many of many')

  const handleChange = (idx: number) => (ev: unknown, val: boolean) => {
    invariant(hasLoadedChildren, 'Children should be loaded')
    const newResponses = { ...currentResponses, [idx]: val }
    setCurrentResponses(newResponses)
  }

  const handleSave = async () => {
    if (!(userId && isPoll(poll) && currentResponses && isPersisted(poll))) {
      throw new Error(`userId must be set`)
    }
    const entity = {
      type: 'pollresponse',
      pollType: 'many-of-many',
      choices: currentResponses,
    } satisfies ManyOfManyPollResponse

    await createEntity({
      entity,
      parent: poll,
    })
  }

  const quota = poll.responseQuota ?? 0
  const isComplete =
    quota > 0
      ? currentResponses
        ? Object.values(currentResponses).reduce((prev, cur) => prev + (cur ? 1 : 0), 0) === quota
        : false
      : true

  const { name = 'Untitled', finalOption, closed } = poll

  const pollOptionsIndices = sort
    ? sortPollOptionsIndices(poll, allResponses, 'random')
    : poll.pollOptions.map((_, idx) => idx)

  /** If the responses object has an entry for us we're done */
  const isResponseSaved = Boolean(finalResponse)

  const { responseQuota = Number.MAX_SAFE_INTEGER } = poll
  const numUserResponses = currentResponses
    ? Object.values(currentResponses).reduce((acc, val) => acc + (val === true ? 1 : 0), 0)
    : 0
  const hasReachedQuota = numUserResponses >= responseQuota

  const readOnly = Boolean(
    !Boolean(onChange) || poll.closed || !hasLoadedChildren || isCreatingEntity || hasCreatedEntity || finalResponse
  )

  const responses = isResponseSaved ? finalResponse : currentResponses

  return (
    <div className={styles.container}>
      <Typography variant="subtitle1" className={styles.title}>
        <PollIcon /> {name}
      </Typography>
      {poll.closed && (
        <Typography variant="caption" color="secondary">
          The Poll is closed.
        </Typography>
      )}
      {!isExternalRespondent && !poll.closed && !isResponseSaved && responseQuota < Number.MAX_SAFE_INTEGER && (
        <Typography variant="caption">
          You have given {numUserResponses} responses of {responseQuota} available.
        </Typography>
      )}
      {isResponseSaved ? (
        <Typography variant="caption" color="primary" display="block">
          {isExternalRespondent ? (respondent?.name ?? respondent?.email + ' has') : 'You have'} completed this poll.
        </Typography>
      ) : null}
      <FormControl className={styles.formControl}>
        <FormGroup>
          {hasLoadedChildren &&
            pollOptionsIndices &&
            pollOptionsIndices
              .map(idx => ({ option: poll.pollOptions[idx], idx }))
              .map(({ option, idx }) => (
                <FormControlLabel
                  key={idx}
                  value={idx}
                  control={
                    <VoteButton
                      disabled={isResponseSaved || readOnly || (hasReachedQuota && !responses?.[idx])}
                      status={responses?.[idx] ?? false}
                      onChange={handleChange(idx)}
                    />
                  }
                  label={
                    <div className={styles.optionLabel}>
                      {option.entityRef ? <EntityLinkById entityId={option.entityRef} /> : option.name}
                      {poll.closed && (
                        <Progress value={getProgress(poll, allResponses, idx)} selected={finalOption?.[idx]} />
                      )}
                    </div>
                  }
                  className={styles.formLabel}
                />
              ))}
        </FormGroup>
        {!isExternalRespondent && !closed ? (
          <Button disabled={!isComplete || isResponseSaved || readOnly} onClick={handleSave}>
            Save my choices
          </Button>
        ) : null}
      </FormControl>
    </div>
  )
}
